/**
 * Common customers controller ESM.
 *
 * Replaces theme/js/modules/common-customers-controller.js (Phase 11).
 * Handles login/register state switching and the forgot-password toggle on
 * customers/login and customers/register. Loaded via entry-customers.esm.js,
 * which gates on template.directory == 'customers', so it runs on every
 * customer page; all three handlers are no-ops when .js-login-or-register is
 * absent (i.e. on account, addresses, order, activate_account, reset_password).
 *
 *   1. loginFocusTrap (turbo:load) — focuses the first visible input in
 *      .js-login-or-register, or triggers the forgot-password toggle when
 *      the URL contains #recover.
 *
 *   2. handleLoginOrRegisterState — .js-state-trigger click changes
 *      data-state on .js-login-or-register and updates the URL pathname.
 *
 *   3. handleForgotPasswordState — .js-toggle-forgot-password click toggles
 *      data-state between 'login' and 'recover' and updates the URL hash.
 *
 * The form submit stub from the IIFE (body was a commented-out postForm call) is dropped.
 *
 * Source path: theme/js/modules/common-customers-controller.esm.js
 * Built name : ./common-customers-controller.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Sonnet 4.6
 *
 * [CommonCustomers]
 */

import { appGet, safeAppCall } from './app-get.js';
import { log } from './log.js';

const LOGIN_OR_REGISTER = '.js-login-or-register';
const STATE_TRIGGER = '.js-state-trigger';
const FORGOT_PASSWORD_TRIGGER = '.js-toggle-forgot-password';
const RECOVER_STATE = 'recover';
const LOGIN_STATE = 'login';

function isVisible(el) {
  return !!el && getComputedStyle(el).display !== 'none';
}

function firstVisibleInput(container) {
  return Array.from(container.querySelectorAll('input')).find(
    i => i.type !== 'hidden' && isVisible(i)
  ) || null;
}

function loginFocusTrap() {
  const el = document.querySelector(LOGIN_OR_REGISTER);
  if (!el || !isVisible(el)) return;
  if (location.hash === '#recover') {
    const toggle = el.querySelector(FORGOT_PASSWORD_TRIGGER);
    if (isVisible(toggle)) toggle.click();
  } else {
    const input = firstVisibleInput(el);
    if (input) input.focus();
  }
}

function handleLoginOrRegisterState(trigger) {
  const el = trigger.closest(LOGIN_OR_REGISTER);
  if (!el) return;
  const targetState = trigger.getAttribute('data-state');
  const currentState = el.getAttribute('data-state');
  if (targetState === currentState) return;
  const url = new URL(appGet('url.current', location.href));
  url.pathname = '/account/' + targetState;
  url.hash = '';
  el.setAttribute('data-state', targetState);
  const input = firstVisibleInput(el);
  if (input) input.focus();
  safeAppCall('replaceState', null, null, url);
}

function handleForgotPasswordState(trigger) {
  const el = trigger.closest(LOGIN_OR_REGISTER);
  if (!el) return;
  const currentState = el.getAttribute('data-state');
  const url = new URL(appGet('url.current', location.href));
  if (currentState !== RECOVER_STATE) {
    el.setAttribute('data-state', RECOVER_STATE);
    url.hash = 'recover';
  } else {
    el.setAttribute('data-state', LOGIN_STATE);
    url.hash = '';
  }
  safeAppCall('replaceState', null, null, url);
  const input = firstVisibleInput(el);
  if (input) input.focus();
}

function handleClick(event) {
  const stateTrigger = event.target.closest(STATE_TRIGGER);
  if (stateTrigger) {
    event.preventDefault();
    handleLoginOrRegisterState(stateTrigger);
    return;
  }
  const forgotTrigger = event.target.closest(FORGOT_PASSWORD_TRIGGER);
  if (forgotTrigger) {
    event.preventDefault();
    handleForgotPasswordState(forgotTrigger);
  }
}

document.addEventListener('turbo:load', loginFocusTrap);
document.addEventListener('click', handleClick);

log('[CommonCustomers] registered');
